import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import type { ResellType, TradeIn } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import TradeInDialog from './trade-in-dialog';

const TradeInEditDialog = ({
  dealId,
  tradeIn,
  dialogIsOpen,
  setDialogIsOpen,
  onSuccess,
  onError,
}: {
  dealId: Deal['id'];
  tradeIn: TradeIn;
  dialogIsOpen: boolean;
  setDialogIsOpen: (dialogIsOpen: boolean) => void;
  onSuccess?: (data: null, variables: { tradeIn: TradeIn }) => void;
  onError?: (error: Error, variables: { tradeIn: TradeIn }) => void;
}) => {
  const queryClient = useQueryClient();

  const updateTradeInMutation = useMutation({
    // onMutate: async ({ tradeIn }) => {
    //   dealStore.updateTradeIn({
    //     id: dealId,
    //     tradeIn: {
    //       ...tradeIn,
    //       deal_id: dealId,
    //     },
    //   });
    // },
    mutationFn: async ({ tradeIn }: { tradeIn: TradeIn }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/trades/${tradeIn.id}/update`, { ...tradeIn })
        .catch((e) => {
          console.error('Error updating trade-in', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      setDialogIsOpen(false);

      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating trade-in', error, variables);
      // TODO (jimmy): toast an error
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  return (
    <TradeInDialog
      key={`trade-in-edit-dialog-${tradeIn.id}`}
      initialVin={tradeIn.vin}
      intialMake={tradeIn.make}
      initialModel={tradeIn.model}
      initialTrim={tradeIn.trim}
      initialYear={tradeIn.year.toString()}
      initialMileage={tradeIn.mileage.toString()}
      initialPaidOff={tradeIn.paid_off}
      initialLienholder={tradeIn.lienholder}
      initialState={tradeIn.state}
      initialPostalCode={tradeIn.postal_code}
      initialNumberOfKeys={tradeIn.number_of_keys}
      initialAskingPrice={tradeIn.asking_price}
      initialExteriorColor={tradeIn.exterior_color}
      initialInteriorColor={tradeIn.interior_color}
      initialOffer={tradeIn.offer}
      initialCondition={tradeIn.condition}
      initialResellType={tradeIn.resell_type}
      onClickCta={({
        vinValue,
        makeValue,
        modelValue,
        trimValue,
        yearValue,
        mileageValue,
        conditionValue,
        paidOffValue,
        lienholderValue,
        stateValue,
        postalCodeValue,
        numberOfKeysValue,
        askingPriceValue,
        exteriorColorValue,
        interiorColorValue,
        offerValue,
        resellTypeValue,
      }: {
        vinValue: string;
        makeValue: string;
        modelValue: string;
        trimValue: string;
        yearValue: string;
        mileageValue: string;
        conditionValue: string;
        paidOffValue: boolean;
        lienholderValue: string;
        stateValue: string;
        postalCodeValue: string;
        numberOfKeysValue: number;
        askingPriceValue: number;
        exteriorColorValue: string;
        interiorColorValue: string;
        offerValue: number;
        resellTypeValue: ResellType;
      }) => {
        updateTradeInMutation.mutate({
          tradeIn: {
            id: tradeIn.id,
            deal_id: dealId,
            vin: vinValue,
            make: makeValue,
            model: modelValue,
            trim: trimValue,
            year: parseInt(yearValue),
            mileage: parseInt(mileageValue),
            condition: conditionValue,
            paid_off: paidOffValue,
            lienholder: lienholderValue,
            state: stateValue,
            postal_code: postalCodeValue,
            number_of_keys: numberOfKeysValue,
            asking_price: askingPriceValue,
            exterior_color: exteriorColorValue,
            interior_color: interiorColorValue,
            offer: offerValue,
            resell_type: resellTypeValue,
          },
        });
      }}
      ctaText={'Save Trade-In'}
      onOpen={() => setDialogIsOpen(true)}
      onClose={() => setDialogIsOpen(false)}
      dialogIsOpen={dialogIsOpen}
    ></TradeInDialog>
  );
};

export default TradeInEditDialog;
